import { formatRelativeTime } from "../date"
import { formatThreadNumber } from "../thread-number"
import { NOTIFICATION_TYPE_COPY, notificationTitles } from "./copy"

// Shared by the dashboard and mobile notification lists so a row reads the same on both.
export const NOTIFICATION_ROW_COPY = {
	navLabel: "Notifications",
	/** Second line for types whose payload has no body; actor and type description follow. */
	typeHints: {
		"docs.proposal_ready": "Accept or decline the change in Docs",
		"import.zendesk_finished": "Open the import to check what came across",
		"import.zendesk_failed": "Open the import to see what stopped it",
		"slack.personal_dm_failed": "Reconnect Slack in your notification settings",
	} as Record<string, string>,
	/** Second line of a thread row: who did what, then the message when there is one. */
	events: {
		wrote: (customer: string, preview: string) =>
			`${customer} wrote: ${preview}`,
		replied: (customer: string) => `${customer} replied`,
		reacted: (customer: string, emoji: string) =>
			`${customer} reacted ${emoji}`,
		assigned: (actor: string) => `${actor} assigned it to you`,
		unassigned: (actor: string) => `${actor} unassigned you`,
		done: (actor: string) => `${actor} marked it done`,
		agentDone: "The agent marked it done",
		reopened: (actor: string) => `${actor} reopened it`,
		draftReady: "The agent drafted a reply",
		handoff: "The agent handed it to your team",
		resolved: "The customer confirmed it's solved",
		resolvedIdle: "The customer went quiet after the answer",
		resolvedJudged:
			"The agent solved it, judged from the finished conversation",
		dueIn: (time: string) => `Reply due in ${time}`,
		overdueBy: (time: string) => `Reply overdue by ${time}`,
		addedTo: (topic: string) => `Added to ${topic}`,
		shipped: (issue: string) => `${issue} shipped`,
		mentioned: (actor: string) => `${actor} mentioned you`,
		commented: (actor: string) => `${actor} commented`,
		linkedTo: (actor: string, target: string) =>
			`${actor} linked it to ${target}`,
		withPreview: (event: string, preview: string) => `${event}: ${preview}`,
	},
	fromActor: (name: string) => `From ${name}`,
	fallbackBody: "Open it to see the details",
}

const copy = NOTIFICATION_ROW_COPY

function payloadText(payload: unknown, key: string): string | null {
	if (typeof payload !== "object" || payload === null) return null
	const value = (payload as Record<string, unknown>)[key]
	return typeof value === "string" && value.trim() ? value.trim() : null
}

interface Person {
	name?: string | null
	email?: string | null
}

export interface NotificationRowCompany {
	name: string
	logoUrl?: string | null
	companyMeta?: { brandfetchData?: unknown } | null
}

/** The live thread a row points at, from the inbox query's thread relation. */
export interface NotificationRowThread {
	number?: number | null
	title?: string | null
	state?: string
	contact?: (Person & { imageUrl?: string | null }) | null
	company?: NotificationRowCompany | null
}

export interface NotificationTextInput {
	type: string
	payload: unknown
	actor?: Person | null
	thread?: NotificationRowThread | null
}

const events = copy.events

/** Teammates go by first name, like Linear; the API member and nameless users by email. */
function teammateName(actor: Person | null | undefined): string | null {
	const first = actor?.name?.trim().split(/\s+/)[0]
	return first || actor?.email || null
}

/** The person who wrote, else their company: "New thread from Acme" beats "from a customer". */
function customerName(thread: NotificationRowThread): string | null {
	return (
		thread.contact?.name?.trim() ||
		thread.contact?.email ||
		thread.company?.name.trim() ||
		null
	)
}

/** The agent closes and reopens threads with no actor, and says so in the payload. */
function byAgent(row: NotificationTextInput): boolean {
	return !row.actor && payloadText(row.payload, "source") === "ai_agent"
}

/** "A customer" when the line opens with it, "a customer" mid-sentence. */
function leadingCustomer(thread: NotificationRowThread): string {
	return customerName(thread) ?? upperFirst(notificationTitles.fallbackCustomer)
}

function withPreview(event: string, preview: string | null) {
	return preview ? events.withPreview(event, preview) : event
}

function upperFirst(text: string) {
	return text.charAt(0).toUpperCase() + text.slice(1)
}

function slaLine(deadline: string | null, now: number): string | null {
	const at = deadline ? new Date(deadline) : null
	if (!at || isNaN(at.getTime())) return null
	const time = formatRelativeTime(at)
	// formatRelativeTime says "now" under a minute.
	const span = time === "now" ? "1min" : time
	return at.getTime() > now ? events.dueIn(span) : events.overdueBy(span)
}

/** Second line of a thread row: who did what, with the message when the event has one. */
function threadEventLine(
	row: NotificationTextInput,
	thread: NotificationRowThread,
	now: number,
): string | null {
	const p = row.payload
	const preview = payloadText(p, "preview")
	const actor = teammateName(row.actor)
	const label = NOTIFICATION_TYPE_COPY[row.type]?.label
	const someone = actor ?? notificationTitles.fallbackActor
	switch (row.type) {
		case "thread.created":
			return preview
				? events.wrote(leadingCustomer(thread), preview)
				: notificationTitles.threadCreated(
						customerName(thread) ?? notificationTitles.fallbackCustomer,
					)
		case "thread.reply_received":
			return withPreview(events.replied(leadingCustomer(thread)), preview)
		case "thread.reaction_received": {
			const emoji = payloadText(p, "emoji")
			if (!emoji) return label ?? null
			const who = payloadText(p, "reactorName") ?? leadingCustomer(thread)
			return events.reacted(who, emoji)
		}
		case "thread.assigned":
			return actor ? events.assigned(actor) : (label ?? null)
		case "thread.unassigned":
			return actor ? events.unassigned(actor) : (label ?? null)
		case "thread.done":
			if (actor) return events.done(actor)
			return byAgent(row) ? events.agentDone : (label ?? null)
		case "thread.reopened":
			return actor ? events.reopened(actor) : (label ?? null)
		case "thread.ai_draft_ready":
			return withPreview(events.draftReady, preview)
		case "thread.ai_handoff":
			return events.handoff
		case "thread.ai_resolved": {
			const reason = payloadText(p, "reason")
			if (reason === "inactivity") return events.resolvedIdle
			if (reason === "judged") return events.resolvedJudged
			if (reason === "customer_confirmed") return events.resolved
			return events.agentDone
		}
		case "thread.sla_at_risk":
		case "thread.sla_breached":
			return slaLine(payloadText(p, "deadlineAt"), now)
		case "thread.topic_added": {
			const topic = payloadText(p, "topicName")
			return topic ? events.addedTo(topic) : (label ?? null)
		}
		case "thread.linked_issue_completed": {
			const issue = payloadText(p, "issueIdentifier")
			return issue ? events.shipped(issue) : (label ?? null)
		}
		case "comment.mentioned":
			return withPreview(events.mentioned(someone), preview)
		case "comment.created":
			return withPreview(events.commented(someone), preview)
		case "issue.request_linked":
		case "project.request_linked": {
			const target =
				payloadText(p, "issueIdentifier") ?? payloadText(p, "projectName")
			if (!target) return null
			return actor
				? events.linkedTo(actor, target)
				: notificationTitles.requestLinked(target)
		}
		default:
			return null
	}
}

export type NotificationAvatarSpec =
	| { kind: "customer"; contact: NonNullable<NotificationRowThread["contact"]> }
	| { kind: "company"; company: NotificationRowCompany }
	| { kind: "actor" }
	| { kind: "agent" }
	| { kind: "icon" }

const AGENT_TYPES = new Set([
	"thread.ai_draft_ready",
	"thread.ai_handoff",
	"thread.ai_resolved",
])
const CUSTOMER_TYPES = new Set([
	"thread.created",
	"thread.reply_received",
	"thread.reaction_received",
])

/**
 * The face on a row: the agent for its own events, the customer for what they wrote,
 * the teammate who acted, and the thread's company when no person did.
 */
export function notificationAvatar(
	row: NotificationTextInput,
): NotificationAvatarSpec {
	if (AGENT_TYPES.has(row.type) || byAgent(row)) return { kind: "agent" }
	const contact = row.thread?.contact
	const company = row.thread?.company
	if (CUSTOMER_TYPES.has(row.type)) {
		if (contact) return { kind: "customer", contact }
		return company ? { kind: "company", company } : { kind: "icon" }
	}
	if (row.actor) return { kind: "actor" }
	if (company) return { kind: "company", company }
	return contact ? { kind: "customer", contact } : { kind: "icon" }
}

/**
 * Thread rows read the live thread: "#43739 Keep the card in HubSpot" over who did what.
 * Everything else, and rows whose thread isn't synced, falls back to the stored snapshot.
 */
export function notificationText(
	row: NotificationTextInput,
	now = Date.now(),
): { title: string; body: string } {
	const thread = row.thread
	const line = thread ? threadEventLine(row, thread, now) : null
	if (thread && line) {
		const title =
			thread.title?.trim() ||
			payloadText(row.payload, "title") ||
			notificationTitles.untitledThread
		const number = formatThreadNumber(thread.number)
		return { title: number ? `${number} ${title}` : title, body: line }
	}
	return storedText(row)
}

/** The stored snapshot, so rows render even for types this client doesn't know. Always two lines. */
function storedText(row: NotificationTextInput): {
	title: string
	body: string
} {
	const actorName = row.actor?.name ?? row.actor?.email
	return {
		title: payloadText(row.payload, "title") ?? copy.navLabel,
		body:
			payloadText(row.payload, "body") ??
			copy.typeHints[row.type] ??
			(actorName ? copy.fromActor(actorName) : null) ??
			NOTIFICATION_TYPE_COPY[row.type]?.description ??
			copy.fallbackBody,
	}
}
